import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between h-16 px-4 md:px-10 max-w-screen-2xl mx-auto">
        <Link
          to="/"
          className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
        >
          <span className="text-lg md:text-xl font-heading font-semibold tracking-tight">gtbuy.wtf</span>
        </Link>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/"
            className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            Home
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                <p className="px-4 py-1.5 text-xs font-medium text-foreground-500 uppercase tracking-wider">14 Product Categories</p>
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded bg-accent-100 text-accent-600 text-xs font-medium flex-shrink-0">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/article/gtbuy-spreadsheet-guide"
            className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            Shopping Guide
          </Link>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            gtbuy sheet
          </a>
        </div>

        <button
          className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-700 cursor-pointer"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 fixed top-16 left-0 right-0 bottom-0 overflow-y-auto z-40">
          <div className="px-4 py-4 space-y-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              Home
            </Link>

            <Link
              to="/article/gtbuy-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              Shopping Guide
            </Link>

            <div className="pt-3 pb-1">
              <p className="px-4 py-1 text-xs font-medium text-foreground-500 uppercase tracking-wider">Categories</p>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-5 h-5 flex items-center justify-center rounded bg-accent-100 text-accent-600 text-xs flex-shrink-0">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="pt-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                gtbuy sheet
                <i className="ri-arrow-right-up-line ml-2 text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}